import { tokens } from "../styles/tokens";
import type { OccupancyCell } from "./OccupancyLayer";

/**
 * Pure placement math for the occupancy loads (ADR 191): where the pallet GLB
 * sits in each occupied bin, how the carton boxes stack on it to the measured
 * height, and the invisible pick volume that keeps the bin clickable through
 * them. Kept free of three.js/React so the whole "what is in the bin, and how
 * tall" decision is unit-tested (mirrors rackFrame.ts vs RackFrameLayer).
 *
 * Frames: inputs are `[[x,y]]` meters in the warehouse frame; outputs are for a
 * NON-rotated world group, where warehouse (x, y) lands at world (x, elevation, y)
 * — the same mapping forklift poses use, and the one OccupancyLayer's rotated
 * floor group resolves to after its y-negation.
 */

/**
 * Status → floor tint. Reuses the existing semantic/ink tokens (no new hex —
 * the design system requires tracing every color back to a token) rather
 * than the forklift `--color-status-*` CSS aliases, which name an unrelated
 * concept (load state, not scan state) despite the coincidental "empty".
 */
export const STATUS_COLOR: Record<OccupancyCell["status"], string> = {
  counted:      tokens.semantic.success, // scanned / ok
  not_scanned:  tokens.count.unvisited,  // "não visitado" (ADR 428: amber read as cardboard)
  empty:        tokens.ink[500],         // neutral / muted
  unidentified: tokens.semantic.danger,  // stock seen, label unreadable (ADR 197)
};

/**
 * Stock present with unknown identity: either the scanner itself reported it
 * (an explicit `unidentified` read, ADR 197) or the rack sensor measured a load
 * while no pass read a label (`empty` + measurement, ADR 154). It gets its own
 * color because it is the only class here that sends someone to a physical
 * location.
 */
export const UNIDENTIFIED_COLOR = tokens.semantic.danger;

/** ADR 428: cartons of a back pallet the camera never read, whose identity the
 *  double-deep rule copied from the front pallet. */
export const INFERRED_COLOR = tokens.count.inferred;

/**
 * Load height to draw where the sensor never fired. Presence is measured (a
 * label was read); the height is not, so this is a nominal pallet rather than a
 * claim — always clamped to the bin's own clear height so a load never grows
 * through the shelf above it.
 */
const NOMINAL_LOAD_M = 1.0;

/**
 * PBR pallet — "Padrão Brasil", 1.00 × 1.20 m, the standard pallet in Brazilian
 * DCs and the module Rua JY's rack is built around: a 1.21 m bin pitch on a
 * 1.50 m deep beam is one PBR pallet plus handling clearance.
 */
const PALLET_L = 1.20;   // along the rack's depth
const PALLET_W = 1.00;   // along the aisle

/**
 * Extracted-part dimensions (ADR 191): `pallet_medium.glb` and `box_carton.glb`
 * are the real `Pallet_Medium`/`NormalBox` parts sliced out of warehouse_UL.glb,
 * normalized to meters, Y-up, origin at the base center. These constants are the
 * measured bounds of those files — placement math and mesh must agree, or a box
 * floats above its pallet.
 */
export const PALLET_MODEL = { length: 1.2, width: 1.0, height: 0.2067 };
export const BOX_MODEL = { footprint: 0.9036, height: 0.7073 };

/**
 * The pallet's footprint inside a bin, in the polygon's own frame.
 *
 * Centred, with its 1.20 m length along the bin's deeper axis (into the rack)
 * and its 1.00 m width along the aisle — derived from the bin's own bounds
 * rather than a global aisle heading, so it stays right for an aisle that does
 * not run the way Rua JY's does.
 *
 * NOT clamped to the polygon (ADR 205): iC3 draws storage locations narrower
 * than the physical position as an operational convention — JSL paints 0,70 m
 * on a 1,20 m pitch (0,50 m painted gap, never under 1,00 m of real space),
 * Flextronics 0,81 m. The drawing carries position and orientation; the pallet
 * is a standard physical object and keeps its 1,00 × 1,20 m.
 */
export function palletRect(polygon: number[][]): { x0: number; x1: number; y0: number; y1: number } | null {
  if (polygon.length < 3) return null;
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  const [w, d] = [x1 - x0, y1 - y0];
  if (w <= 0 || d <= 0) return null;
  const [nx, ny] = w >= d ? [PALLET_L, PALLET_W] : [PALLET_W, PALLET_L];
  const [cx, cy] = [(x0 + x1) / 2, (y0 + y1) / 2];
  return { x0: cx - nx / 2, x1: cx + nx / 2, y0: cy - ny / 2, y1: cy + ny / 2 };
}

/** A bin is drawn as a solid load when something is physically in it; null when
 *  the bin is genuinely empty or was never visited. Exported: this is the whole
 *  "what is in the bin, and how tall" decision, and it is worth asserting
 *  directly rather than through a stringified geometry arg. */
export function loadOf(cell: OccupancyCell): { height: number; color: string } | null {
  const measured = cell.loadHeightM ?? null;
  if (cell.status === "counted") {
    return { height: clampLoad(measured ?? NOMINAL_LOAD_M, cell), color: STATUS_COLOR.counted };
  }
  // Explicitly unidentified (ADR 197): occupied, identity unknown — a load is
  // drawn (nominal height when the sensor never fired) so the bin reads full.
  if (cell.status === "unidentified") {
    return { height: clampLoad(measured ?? NOMINAL_LOAD_M, cell), color: UNIDENTIFIED_COLOR };
  }
  // "empty" + a real measurement = stock present, identity unknown (ADR 154).
  if (cell.status === "empty" && measured != null && measured > 0) {
    return { height: clampLoad(measured, cell), color: UNIDENTIFIED_COLOR };
  }
  return null;
}

function clampLoad(height: number, cell: OccupancyCell): number {
  const clear = cell.clearHeightM ?? null;
  return clear != null && clear > 0 ? Math.min(height, clear) : height;
}

export interface PalletPlacement {
  key: string;
  /** Pallet-rect center, warehouse frame. */
  x: number;
  y: number;
  /** Bin floor elevation — the instance's world y (model origin is its base). */
  elev: number;
  /** 0 = model length along x; π/2 = along y. */
  rotY: number;
}

/** A box instance's bin address — the key is `${address}:${layer}` (below). */
export function boxAddress(b: Pick<BoxPlacement, "key">): string {
  return b.key.slice(0, b.key.lastIndexOf(":"));
}

export interface BoxPlacement {
  key: string;
  x: number;
  y: number;
  /** World y of THIS layer's base (model origin is its base). */
  elev: number;
  rotY: number;
  /** [model x, height, model z] scale. */
  scale: [number, number, number];
  /** Unidentified stock (ADR 154) — tinted UNIDENTIFIED_COLOR instead of natural. */
  danger: boolean;
  /** Inferred back pallet (ADR 428) — tinted INFERRED_COLOR instead of natural. */
  inferred: boolean;
  /** The cell's WMS expiry (ADR 203), carried so the Validade paint mode
   *  (ADR 354) can tint cartons without a lookup back into the cells. */
  expiryDate: string | null;
  /** The cell's WMS fifo/entry date, same carry for the Idade mode (ADR 361). */
  fifoDate: string | null;
}

export interface PickVolume {
  cell: OccupancyCell;
  /** Bin bbox center, warehouse frame. */
  x: number;
  y: number;
  elev: number;
  /** World x/z extents of the bin bbox and the load's height. */
  w: number;
  d: number;
  h: number;
}

export interface LoadBuild {
  pallets: PalletPlacement[];
  boxes: BoxPlacement[];
  picks: PickVolume[];
}

/**
 * Spatial buckets for instanced rendering (ADR 199). One InstancedMesh per
 * ~`cellM` grid cell lets three.js frustum-cull whole blocks of a large
 * warehouse once each mesh carries its real instance bounds — a single
 * warehouse-wide mesh can never be culled, so zooming into one aisle still
 * paid vertex work for all 13k loads at JSL scale.
 */
export const CHUNK_CELL_M = 24;

export function chunkByGrid<T extends { x: number; y: number }>(
  items: T[], cellM: number = CHUNK_CELL_M,
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const it of items) {
    const key = `${Math.floor(it.x / cellM)},${Math.floor(it.y / cellM)}`;
    const bucket = out.get(key);
    if (bucket) bucket.push(it);
    else out.set(key, [it]);
  }
  return out;
}

/**
 * Distance LOD for the load parts (ADR 199), decided PER CHUNK: a chunk whose
 * center sits past FAR_ENTER_M swaps the GLB pallet and carton (182/50 tris)
 * for 12-tri proxy boxes — at that range a load is a handful of pixels and
 * only the silhouette reads, while the whole-warehouse view carried 3,2 M
 * triangles of invisible detail. Per chunk (not one global switch) so zooming
 * into an aisle details the racks in front of the camera and keeps the rest of
 * the warehouse cheap. The two thresholds are hysteresis so orbiting at the
 * boundary never flaps a chunk.
 */
export const FAR_ENTER_M = 55;
export const FAR_EXIT_M = 45;

export function lodFar(distanceM: number, wasFar: boolean): boolean {
  return wasFar ? distanceM > FAR_EXIT_M : distanceM > FAR_ENTER_M;
}

/** Center of a `chunkByGrid` cell, from its `"i,j"` key. */
export function chunkCenter(key: string, cellM: number = CHUNK_CELL_M): { x: number; y: number } {
  const [i, j] = key.split(",").map(Number);
  return { x: (i + 0.5) * cellM, y: (j + 0.5) * cellM };
}

/**
 * One pallet per occupied racked bin, boxes stacked on its deck up to the
 * measured (or nominal) load height, and a pick volume so clicking the goods
 * still selects the bin.
 *
 * Stacking: the space above the deck splits into `round(available / boxH)`
 * layers (min 1), each layer one carton scaled vertically to fill its share —
 * a 1.4 m load reads as two cartons, not one stretched 4× or a void. A load no
 * taller than the pallet itself renders as the bare pallet (the deck IS the
 * measurement then). Flat bins (`elevationM` null) draw nothing here — the
 * floor-plan tint alone remains their reading, as before.
 */
export function buildLoads(cells: OccupancyCell[]): LoadBuild {
  const pallets: PalletPlacement[] = [];
  const boxes: BoxPlacement[] = [];
  const picks: PickVolume[] = [];
  const fronts = frontDirections(cells);

  // One pallet on its deck, cartons stacked to the load height.
  const place = (key: string, cell: OccupancyCell, x: number, y: number, alongX: boolean,
                 load: { height: number; color: string }, inferred = false) => {
    const elev = cell.elevationM as number;
    pallets.push({ key, x, y, elev, rotY: alongX ? 0 : Math.PI / 2 });
    const danger = load.color === UNIDENTIFIED_COLOR;
    const available = load.height - PALLET_MODEL.height;
    if (available <= 0) return;
    const layers = Math.max(1, Math.round(available / BOX_MODEL.height));
    const layerH = available / layers;
    // The carton's 0.90 m footprint sits inside the 1.00 m pallet width, so a
    // full-size deck never needs a horizontal clamp on its goods.
    for (let i = 0; i < layers; i++) {
      boxes.push({
        key: `${key}:${i}`,
        x, y,
        elev: elev + PALLET_MODEL.height + i * layerH,
        rotY: alongX ? 0 : Math.PI / 2,
        scale: [1, layerH / BOX_MODEL.height, 1],
        danger,
        inferred,
        expiryDate: cell.expiryDate ?? null,
        fifoDate: cell.fifoDate ?? null,
      });
    }
  };

  for (const cell of cells) {
    if (cell.elevationM == null) continue;
    const rect = palletRect(cell.polygon);
    if (!rect) continue;
    const [rw, rd] = [rect.x1 - rect.x0, rect.y1 - rect.y0];
    const alongX = rw >= rd; // pallet length axis, mirrors palletRect's choice

    const xs = cell.polygon.map((p) => p[0]);
    const ys = cell.polygon.map((p) => p[1]);
    const [bx0, bx1] = [Math.min(...xs), Math.max(...xs)];
    const [by0, by1] = [Math.min(...ys), Math.max(...ys)];

    let height: number;
    if (cell.depthSlots === 2 && cell.pallets) {
      // ADR 425: each physical pallet in its half of the bay, front = corridor side.
      if (!cell.pallets.length) continue;
      const half = (alongX ? bx1 - bx0 : by1 - by0) / 4;
      const sign = fronts.get(cell.address) ?? -1;
      const [cx, cy] = [(bx0 + bx1) / 2, (by0 + by1) / 2];
      height = 0;
      for (const p of cell.pallets) {
        const load = loadOf({ ...cell, status: p.status });
        if (!load) continue;
        const off = (p.depth === "front" ? sign : -sign) * half;
        place(`${cell.address}#${p.depth}`, cell, alongX ? cx + off : cx, alongX ? cy : cy + off, alongX, load,
              p.inferred === true);
        height = Math.max(height, load.height);
      }
    } else {
      const load = loadOf(cell);
      if (!load) continue;
      place(cell.address, cell, (rect.x0 + rect.x1) / 2, (rect.y0 + rect.y1) / 2, alongX, load);
      height = load.height;
    }

    // The pallet can be wider than the painted location (ADR 205): the pick
    // volume covers whichever is larger, or clicking a pallet edge would miss.
    picks.push({
      cell,
      x: (bx0 + bx1) / 2, y: (by0 + by1) / 2, elev: cell.elevationM,
      w: Math.max(bx1 - bx0, alongX ? PALLET_MODEL.length : PALLET_MODEL.width),
      d: Math.max(by1 - by0, alongX ? PALLET_MODEL.width : PALLET_MODEL.length),
      h: Math.max(height, PALLET_MODEL.height),
    });
  }

  return { pallets, boxes, picks };
}

/**
 * ADR 425: which way the front (corridor side) of each double-deep bin faces, as the
 * sign along the bin's depth axis (its longer side). Per aisle, the corridor is the
 * widest gap between the bins' depth centres, as `zones-pallet-footprint.py` builds
 * the footprints; a bin's front faces it. An aisle with one face only has no gap to
 * find, and its front falls back to the lower coordinate.
 */
export function frontDirections(cells: OccupancyCell[]): Map<string, 1 | -1> {
  const groups = new Map<string, { address: string; c: number; depth: number }[]>();
  for (const cell of cells) {
    if (cell.depthSlots !== 2 || cell.polygon.length < 3) continue;
    const xs = cell.polygon.map((p) => p[0]);
    const ys = cell.polygon.map((p) => p[1]);
    const [w, d] = [Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)];
    const alongX = w >= d;
    const c = alongX ? (Math.max(...xs) + Math.min(...xs)) / 2 : (Math.max(...ys) + Math.min(...ys)) / 2;
    const key = `${cell.aisleLabel ?? ""}|${alongX ? "x" : "y"}`;
    const g = groups.get(key) ?? [];
    g.push({ address: cell.address, c, depth: Math.max(w, d) });
    groups.set(key, g);
  }
  const out = new Map<string, 1 | -1>();
  for (const g of groups.values()) {
    const cs = [...new Set(g.map((b) => b.c))].sort((a, b) => a - b);
    let split: number | null = null;
    let widest = Math.max(...g.map((b) => b.depth)) / 2;   // a gap inside one face is no corridor
    for (let i = 1; i < cs.length; i++) {
      if (cs[i] - cs[i - 1] > widest) { widest = cs[i] - cs[i - 1]; split = (cs[i] + cs[i - 1]) / 2; }
    }
    for (const b of g) out.set(b.address, split != null && b.c < split ? 1 : -1);
  }
  return out;
}
